import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Cpu,
  Info,
  Star,
  Server,
  Database,
  AlertTriangle
} from "lucide-react";
import { type CalculationResult, USE_CASES } from "@/types/calculator";
import { VramBreakdownChart } from "@/components/calculator/vram-breakdown-chart";
import { ChecksCard, PowerCard } from "@/components/calculator/deployment-cards";
import { approxTime, lowConfidenceNote } from "@/lib/latency";

interface OutputPanelProps {
  result: CalculationResult | null;
  isCalculating: boolean;
  sessionRows: Record<string, string | number>[];
}

export function OutputPanel({ result, isCalculating }: OutputPanelProps) {
  if (isCalculating) {
    return (
      <div className="space-y-6">
        <Card className="transition-all duration-300 hover:shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-8 text-center">
            <div className="mb-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lenovo-red mx-auto" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Calculating Requirements</h3>
            <p className="text-gray-500 dark:text-gray-400">Please wait while we analyze your LLM requirements...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="space-y-6">
        <Card className="transition-all duration-300 hover:shadow-lg bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
          <CardContent className="p-8 text-center">
            <div className="mb-4">
              <Cpu className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto" />
            </div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Configure your requirements</h3>
            <p className="text-gray-500 dark:text-gray-400 mb-4">Select a use case and click "Calculate GPU Requirements" to see recommendations</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const useCase = USE_CASES[result.inputSummary.useCase as keyof typeof USE_CASES];
  const cardCls = "transition-all duration-300 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700";
  const labelCls = "text-gray-600 dark:text-gray-400";
  const valueCls = "font-medium text-gray-900 dark:text-gray-100";
  const headingCls = "text-lg font-bold text-gray-900 dark:text-white";
  const subheadingCls = "font-medium text-gray-900 dark:text-white";
  // Per-server figure, with the total when there are several GPU servers.
  const perNode = (each: number | undefined, total: number, unit: string) =>
    each && (result.cpuRequirements.nodes ?? 1) > 1 ? `${each.toLocaleString()} ${unit}/node (${total.toLocaleString()} ${unit} total)` : `${total} ${unit}`;
  return (
    <div className="space-y-6">
      <p className="text-xs text-gray-500 mt-2">
        Disclaimer: These values are approximate estimates based on provided inputs and may not reflect exact performance.
      </p>

      {/* Insufficient-VRAM (or other) warning */}
      {result.warning && (
        <div className="flex items-start space-x-2 rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/30 p-4">
          <AlertTriangle className="w-5 h-5 text-lenovo-red shrink-0 mt-0.5" />
          <p className="text-sm text-red-800 dark:text-red-200">{result.warning}</p>
        </div>
      )}

      {/* Input Summary */}
      <Card className={`${cardCls} hover:shadow-lg`}>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Info className="w-5 h-5 text-lenovo-blue" />
            <h3 className={headingCls}>Input Summary</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Use Case:</span>
              <span className="font-medium ml-2">{useCase}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Model:</span>
              <span className="font-medium ml-2 capitalize"> {result.inputSummary.modelFamily === "custom" ? (result.inputSummary.customModelName || "Custom") : result.inputSummary.modelFamily}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">RAG Enabled:</span>
              <span className="font-medium ml-2">{result.inputSummary.enableRAG ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">High Availabilty:</span>
              <span className="font-medium ml-2">{result.inputSummary.highAvailability ? 'Yes' : 'No'}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Precision:</span>
              <span className="font-medium ml-2">{result.inputSummary.precision.toUpperCase()}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Concurrent Users:</span>
              <span className="font-medium ml-2">{result.inputSummary.concurrentUsers}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Requests / User / Min:</span>
              <span className="font-medium ml-2">{result.inputSummary.requestsPerUserPerMinute ?? "—"}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Tokens:</span>
              <span className="font-medium ml-2">{result.inputSummary.inputTokens}/{result.inputSummary.outputTokens}</span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Parallelism:</span>
              <span className="font-medium ml-2">
                TP = {result.tensorParallelism ?? result.recommendedConfig.quantity}
                {(result.pipelineParallelism ?? 1) > 1 ? ` · PP = ${result.pipelineParallelism}` : ""}
                {(result.vramBreakdown.copies ?? 1) > 1 ? ` · ${result.vramBreakdown.copies} copies` : ""}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Recommended GPU Configuration */}
      <Card className={`${cardCls} hover:shadow-xl hover:shadow-yellow-200/30`}>
        <CardContent className="p-6">
          {/* Header */}
          <div className="flex items-center space-x-2 mb-4">
            <Star className="w-5 h-5 text-yellow-500" />
            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
              Recommended GPU Configuration
            </h3>
          </div>
          
          {/* Optimal Configuration */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700/40 rounded-lg p-4 mb-6">
            {/* GPU Details */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <div>
                <span className={labelCls}>GPU:</span>
                <div className="font-bold text-lg text-gray-900 dark:text-white">{result.recommendedConfig.name}</div>
              </div>
              <div>
                <span className={labelCls}>Quantity:</span>
                <div className="font-bold text-lg text-gray-900 dark:text-white">{result.recommendedConfig.quantity} {result.recommendedConfig.quantity === 1 ? "GPU" : "GPUs"}</div>
              </div>
              <div>
                <span className={labelCls}>VRAM/GPU:</span>
                <div className="font-bold text-lg text-gray-900 dark:text-white">{result.recommendedConfig.vramPerGpu} GB</div>
              </div>
              <div>
                <span className={labelCls}>Total VRAM:</span>
                <div className="font-bold text-lg text-lenovo-red">{result.recommendedConfig.totalVram} GB</div>
              </div>
            </div>

          </div>

          <VramBreakdownChart result={result} />
        </CardContent>
      </Card>

      {/* Server Recommendations */}
      <Card className="transition-all duration-300 hover:shadow-lg hover:shadow-gray-200/50">
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Database className="w-5 h-5 text-lenovo-blue" />
            <h3 className={headingCls}>Server Configuration</h3>
          </div>
          <div className="bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <span className={`font-medium ${subheadingCls}`}>Recommended Server Type</span>
              <Badge className="bg-lenovo-red text-white">{result.serverConfiguration.category}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div><span className={labelCls}>Compute:</span><div className={valueCls}>{result.serverConfiguration.compute}</div></div>
              <div><span className={labelCls}>Storage:</span><div className={valueCls}>{result.serverConfiguration.storage}</div></div>
              <div><span className={labelCls}>Network:</span><div className={valueCls}>{result.serverConfiguration.network}</div></div>
              <div><span className={labelCls}>Switching:</span><div className={valueCls}>{result.serverConfiguration.networking}</div></div>
            </div>
            {!!result.serverConfiguration.rsus && (
              <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
                {result.serverConfiguration.rsus} scalable unit{result.serverConfiguration.rsus === 1 ? "" : "s"} (up to 4 compute nodes each)
                {result.serverConfiguration.gpusPerNode ? ` · ${result.serverConfiguration.gpusPerNode} GPUs per node` : ""}
              </p>
            )}

            {result.serverConfiguration.platform && (
              <div className="mt-4 border-l-4 border-lenovo-red bg-red-50 dark:bg-red-950/30 rounded-r-lg px-3.5 py-3">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Recommended platform: {result.serverConfiguration.platform}
                    {result.serverConfiguration.platform_tier && (
                      <span className="ml-2 align-middle text-[11px] font-medium px-2 py-0.5 rounded-full bg-white dark:bg-gray-800 border border-red-200 dark:border-red-900 text-gray-700 dark:text-gray-300">
                        {result.serverConfiguration.platform_tier}
                      </span>
                    )}
                  </span>
                  {result.serverConfiguration.platform_url && (
                    <a
                      href={result.serverConfiguration.platform_url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs font-medium text-lenovo-blue hover:underline"
                    >
                      Platform guide &rarr;
                    </a>
                  )}
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  {[result.serverConfiguration.platform_spec, result.serverConfiguration.platform_servers]
                    .filter(Boolean)
                    .join("  ·  ")}
                </div>
                {result.serverConfiguration.platform_best_for && (
                  <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                    Best for {result.serverConfiguration.platform_best_for}.
                  </div>
                )}
                {result.serverConfiguration.platform_rationale && (
                  <div className="text-[11px] text-lenovo-gray mt-1.5">
                    Sized as a {result.serverConfiguration.platform_rationale} deployment.
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <PowerCard result={result} />

      {/* Performance Metrics */}
      <Card className={`${cardCls} hover:shadow-lg hover:shadow-green-200/30`}>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Cpu className="w-5 h-5 text-green-600 dark:text-green-400" />
            <h3 className={headingCls}>Performance Metrics</h3>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-600 dark:text-gray-400">Time to First Token (TTFT):</span>
              <span className="font-medium ml-2">
                {approxTime(result.otherParameters.prefill_time)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Intertoken Latency (ITL):</span>
              <span className="font-medium ml-2">
                {approxTime(result.otherParameters.generation_time)}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Request Latency (E2E Latency):</span>
              <span className="font-medium ml-2">
                {approxTime(result.otherParameters.estimated_response_time, "sec")}
              </span>
            </div>
            <div>
              <span className="text-gray-600 dark:text-gray-400">Max Concurrent Users:</span>
              <span className="font-medium ml-2">{result.otherParameters.max_conc_users}</span>
            </div>
            {result.otherParameters.activeRequests != null && (
              <p className="col-span-2 text-xs text-gray-500 dark:text-gray-400">
                Latency is for ~{result.otherParameters.activeRequests} request{result.otherParameters.activeRequests === 1 ? "" : "s"} in flight
                {result.inputSummary.requestsPerUserPerMinute
                  ? ` (${result.inputSummary.concurrentUsers} users × ${result.inputSummary.requestsPerUserPerMinute} requests/min each)`
                  : " (every user waiting at once)"}
                ; memory is sized for all {result.inputSummary.concurrentUsers} users at once.
              </p>
            )}
            {lowConfidenceNote(result) && (
              <p className="col-span-2 flex items-start gap-1.5 text-xs text-amber-700 dark:text-amber-400">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" aria-hidden />
                {lowConfidenceNote(result)}
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* System & CPU Requirements */}
      <Card className={`${cardCls} hover:shadow-lg`}>
        <CardContent className="p-6">
          <div className="flex items-center space-x-2 mb-4">
            <Server className="w-5 h-5 text-lenovo-blue" />
            <h3 className={headingCls}>System & CPU Requirements</h3>
          </div>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <h4 className={`font-medium mb-3 ${subheadingCls}`}>Memory Requirements</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={labelCls}>Model Memory:</span>
                  <span className={valueCls}>{result.memoryRequirements.model} GB</span>
                </div>
                <div className="flex justify-between">
                  <span className={labelCls}>RAG Memory:</span>
                  <span className={valueCls}>{result.memoryRequirements.rag} GB</span>
                </div>
                <div className="flex justify-between border-t border-gray-200 dark:border-gray-600 pt-2">
                  <span className={subheadingCls}>Total Required:</span>
                  <span className="font-bold text-lenovo-red">{result.memoryRequirements.total} GB</span>
                </div>
              </div>
            </div>
            <div>
              <h4 className={`font-medium mb-3 ${subheadingCls}`}>CPU Requirements</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className={labelCls}>CPU Cores:</span>
                  <span className={valueCls}>{perNode(result.cpuRequirements.coresPerNode, result.cpuRequirements.cores, "cores")}</span>
                </div>
                <div className="flex justify-between">
                  <span className={labelCls}>System RAM:</span>
                  <span className={valueCls}>{perNode(result.cpuRequirements.ramPerNode, result.cpuRequirements.ram, "GB")}</span>
                </div>
                <div className="flex justify-between">
                  <span className={labelCls}>Storage:</span>
                  <span className={valueCls}>{result.cpuRequirements.storage}</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <ChecksCard result={result} />
    </div>
  );
}